import * as models from "./api/models";
import isAddressKey from "./isAddressKey";
import isApplicationKey from "./isApplicationKey";
import isReviewKey from "./isReviewKey";
import isSecuredDepositKey from "./isSecuredDepositKey";
import validateAddressField from "./validateAddressField";
import {EMPTY_VALUE} from "../components/Select";

export type InputValidation = {
    isValid: boolean;
    messages: string[];
    validationKey:
        | models.AppKeys
        | models.AddressValidationKeys
        | models.ReviewKeys
        | models.SecuredDepositKeys
        | models.LookupAppKeys
        | null;
};

const EMPTY_ARR: string[] = [];
const NOT_FOUND_VALIDATION = {isValid: true, messages: EMPTY_ARR, validationKey: null};

const SELECT_INPUTS = [
    models.AppKeys.StudentStatus,
    models.AppKeys.SavingsAccount,
    models.AppKeys.PaymentAccount,
    models.AppKeys.Residency,
];

const REQUIRED_INPUTS = [
    models.AppKeys.Email,
    models.AppKeys.FirstName,
    models.AppKeys.LastName,
    models.AppKeys.PrimaryPhone,
    models.AppKeys.DateOfBirth,
    models.AppKeys.SocialSecurity,
    models.AppKeys.College,
    models.AppKeys.AccountTotal,
    models.AppKeys.AnnualGrossIncome,
    models.ReviewKeys.Text,
    models.ReviewKeys.Title,
    models.ReviewKeys.Email,
];

const strings = {
    required: "Value is required.",
    invalidFormat: "is not a valid value.",
    dateFormat: "Dates should be in the MM/DD/YYYY format.",
    ssFormat: "Social Security should be in the XXX-XX-XXXX format.",
};

const REGEX = {
    email: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
    zip: /[0-9]{5}/,
    phone: /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/,
    birthDate: /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/,
    socialSecurity: /^\d{3}-\d{2}-\d{4}$/,
};

export function isValidDate(dateString: string): boolean {
    // Parse the date parts to integers
    const parts = dateString.split("/");
    const day = parseInt(parts[1], 10);
    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[2], 10);

    // Check the ranges of month and year
    if (year < 1000 || year > 3000 || month === 0 || month > 12) return false;

    const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Adjust for leap years
    if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) monthLength[1] = 29;

    // Check the range of the day
    return day > 0 && day <= monthLength[month - 1];
}

export default function validateApplicationField(
    name: models.AppKeys | models.AddressKeys | models.ReviewKeys,
    value: string | boolean,
): InputValidation {
    if (isAddressKey(name)) {
        return validateAddressField(name, value);
    }
    const validationKey = name;
    if (
        !isAddressKey(name) &&
        !isApplicationKey(name) &&
        !isReviewKey(name) &&
        !isSecuredDepositKey(name)
    ) {
        return NOT_FOUND_VALIDATION;
    }

    if (SELECT_INPUTS.find((selectName) => name === selectName)) {
        const isValid = value !== EMPTY_VALUE;
        return {
            isValid,
            messages: isValid ? EMPTY_ARR : [strings.required],
            validationKey: validationKey,
        };
    }

    const validation = {
        isValid: true,
        messages: EMPTY_ARR,
        validationKey,
    };

    if (REQUIRED_INPUTS.find((reqdName) => name === reqdName)) {
        if (typeof value === "string" && !value) {
            validation.isValid = false;
            validation.messages = [...validation.messages, strings.required];
        }
    }

    if (
        (name === models.AppKeys.Email || name === models.ReviewKeys.Email) &&
        typeof value === "string" &&
        !!value
    ) {
        const invalid = !value.match(REGEX.email);
        if (invalid) {
            validation.isValid = false;
            validation.messages = [...validation.messages, `"${value}" ${strings.invalidFormat}`];
        }
    }

    if (name === models.AppKeys.PrimaryPhone && typeof value === "string" && !!value) {
        const invalid = !value.match(REGEX.phone);
        if (invalid) {
            validation.isValid = false;
            validation.messages = [...validation.messages, `"${value}" ${strings.invalidFormat}`];
        }
    }

    if (name === models.AppKeys.DateOfBirth && typeof value === "string" && !!value) {
        const invalid = !value.match(REGEX.birthDate) || !isValidDate(value);
        if (invalid) {
            validation.isValid = false;
            validation.messages = [
                ...validation.messages,
                `"${value}" ${strings.invalidFormat}`,
                !isValidDate(value) ? "" : strings.dateFormat,
            ];
        }
    }

    if (name === models.AppKeys.AnnualObligation && typeof value === "string" && !value) {
        validation.isValid = false;
        validation.messages = [...validation.messages, `"${value}" ${strings.invalidFormat}`];
    }

    if (name === models.AppKeys.SocialSecurity && typeof value === "string" && !!value) {
        const invalid = !value.match(REGEX.socialSecurity);
        if (invalid) {
            validation.isValid = false;
            validation.messages = [
                ...validation.messages,
                `"${value}" ${strings.invalidFormat}`,
                strings.ssFormat,
            ];
        }
    }

    return validation;
}
